import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useAppSelector } from '../../../../redux/ReduxHooks';
import { selectAreDrivebaseCommandActionsDisabled, selectIsProgramActive } from '../../../../redux/RobotSlice';
import { electronics } from '../../../../utils/constants';
import { MOTOR_DIRECTION, type MotorDirection } from '../../../../utils/motor-direction';
import { HighSpeedMotorIcon, HighTorqueMotorIcon } from '../../../Icons/Bits';

interface MotorCheckDialogProps {
  open: boolean;
  port?: number;
  electronicType?: string;
  onClose: () => void;
}

function getDefaultVelocity(maxRpm: number) {
  return Math.max(1, Math.round(maxRpm / 2));
}

function formatTargetRpm(value: number) {
  return `${Math.round(Math.abs(value))} rpm`;
}

function formatCurrentVelocityRpm(value: number) {
  const roundedValue = Math.round(value);
  return `${Object.is(roundedValue, -0) ? 0 : roundedValue} rpm`;
}

function degreesPerSecondToRpm(value: number) {
  return value / 6;
}

const MotorCheckDialog = ({ open, port, electronicType, onClose }: MotorCheckDialogProps) => {
  const isHighTorqueMotor = electronicType === electronics.highTorqueMotor.ELECTRONIC_TYPE;
  const motorTitle = isHighTorqueMotor ? 'High-Torque Motor' : 'High-Speed Motor';
  const maxRpm = isHighTorqueMotor ? electronics.highTorqueMotor.MAX_RPM : electronics.highSpeedMotor.MAX_RPM;
  const HeaderIcon = isHighTorqueMotor ? HighTorqueMotorIcon : HighSpeedMotorIcon;
  const defaultVelocity = useMemo(() => getDefaultVelocity(maxRpm), [maxRpm]);
  const [rpm, setRpm] = useState(defaultVelocity);
  const [direction, setDirection] = useState<MotorDirection>(MOTOR_DIRECTION.forward);
  const [isStarting, setIsStarting] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [didObserveActiveRun, setDidObserveActiveRun] = useState(false);
  const [currentVelocityRpm, setCurrentVelocityRpm] = useState(0);
  const isProgramActive = useAppSelector(selectIsProgramActive);
  const commandActionsDisabled = useAppSelector(selectAreDrivebaseCommandActionsDisabled);

  const resetRunState = () => {
    setIsStarting(false);
    setIsRunning(false);
    setDidObserveActiveRun(false);
  };

  useEffect(() => {
    if (!open) return;
    setRpm(defaultVelocity);
    setDirection(MOTOR_DIRECTION.forward);
    setIsStarting(false);
    setIsRunning(false);
    setDidObserveActiveRun(false);
    setCurrentVelocityRpm(0);
  }, [defaultVelocity, open]);

  useEffect(() => {
    if (!open || port == null || !window.robotService?.getMotorPlusSettings) {
      setCurrentVelocityRpm(0);
      return;
    }

    let isCancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const pollMotorSettings = async () => {
      try {
        const settings = await window.robotService?.getMotorPlusSettings?.(port);
        if (!isCancelled && settings?.velocity != null) {
          setCurrentVelocityRpm(degreesPerSecondToRpm(settings.velocity));
        }
      } catch {
        if (!isCancelled) {
          setCurrentVelocityRpm(0);
        }
      } finally {
        if (!isCancelled) {
          timeoutId = setTimeout(pollMotorSettings, 500);
        }
      }
    };

    void pollMotorSettings();

    return () => {
      isCancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [open, port]);

  useEffect(() => {
    if (isProgramActive) {
      setDidObserveActiveRun(true);
      if (isStarting) {
        setIsStarting(false);
        setIsRunning(true);
      }
      return;
    }

    if (didObserveActiveRun && (isRunning || isStarting)) {
      setIsRunning(false);
      setIsStarting(false);
      setDidObserveActiveRun(false);
    }
  }, [didObserveActiveRun, isProgramActive, isRunning, isStarting]);

  const handleStart = async () => {
    if (port == null || !electronicType || commandActionsDisabled) return;
    setIsStarting(true);

    try {
      await window.robotService?.startMotorCheck?.({
        port,
        electronicType,
        rpm,
        direction,
      });
      setIsRunning(true);
    } catch (error) {
      console.error('Failed to start motor check', error);
      setIsRunning(false);
      setIsStarting(false);
    }
  };

  const handleStop = async () => {
    try {
      if (port != null) {
        await window.robotService?.stopMotorCheck?.(port);
      }
    } catch (error) {
      console.error('Failed to stop motor check', error);
    } finally {
      resetRunState();
    }
  };

  const handleClose = async () => {
    try {
      if ((isRunning || isStarting) && port != null) {
        await window.robotService?.stopMotorCheck?.(port);
      }
    } catch (error) {
      console.error('Failed to stop motor check before closing', error);
    } finally {
      resetRunState();
      onClose();
    }
  };

  return (
    <>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={0}>
          <Box
            component={HeaderIcon}
            aria-hidden
            sx={{
              width: 28,
              height: 28,
              display: 'block',
              color: 'primary.main',
              mr: 1,
            }}
          />
          <Box component="span" sx={{ ml: 0.5 }}>
            {port != null ? `${motorTitle} (Port ${port})` : motorTitle}
          </Box>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ width: 500, minHeight: 380 }}>
        <Stack spacing={3}>
          <Typography>
            <Trans i18nKey="motorCheckDialog.main-text">Review the output from your motor to confirm functionality.</Trans>
          </Typography>

          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h6" component="div">
              <Trans i18nKey="motorCheckDialog.currentVelocity">Current Velocity:</Trans>
            </Typography>
            <Box
              sx={{
                minWidth: 140,
                px: 3,
                py: 1,
                borderRadius: 2,
                bgcolor: 'background.default',
                color: 'text.primary',
                textAlign: 'center',
                boxShadow: 1,
              }}
            >
              <Typography variant="h6" component="div" fontWeight={700}>
                {formatCurrentVelocityRpm(currentVelocityRpm)}
              </Typography>
            </Box>
          </Stack>

          <Stack spacing={1}>
            <Box sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="h6" component="div">
                <Trans i18nKey="motorCheckDialog.targetVelocity">Target Velocity:</Trans>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {formatTargetRpm(rpm)}
              </Typography>
            </Box>
            <Box>
              <Box sx={{ boxSizing: 'border-box', minWidth: 0, pl: 2, pr: 3, width: '100%' }}>
                <Slider
                  value={rpm}
                  min={0}
                  max={maxRpm}
                  step={1}
                  marks={[{ value: 0 }, { value: maxRpm }]}
                  valueLabelDisplay="off"
                  disabled={isRunning || isStarting}
                  onChange={(_event, value) => setRpm(Array.isArray(value) ? value[0] ?? 0 : value)}
                  sx={{
                    display: 'block',
                    width: '100%',
                    maxWidth: '100%',
                    '& .MuiSlider-mark': { bgcolor: 'action.disabled' },
                    '& .MuiSlider-markActive': { bgcolor: 'action.disabled' },
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', color: 'text.secondary', mt: -0.5 }}>
                  <Typography variant="body2">{formatTargetRpm(0)}</Typography>
                  <Typography variant="body2">{formatTargetRpm(maxRpm)}</Typography>
                </Box>
              </Box>
            </Box>
          </Stack>

          <Stack direction="column" spacing={1.5}>
            <InputLabel id="motor-check-direction-label">
              <Typography variant="h6" component="div">
                <Trans i18nKey="motorCheckDialog.direction">Motor Direction:</Trans>
              </Typography>
            </InputLabel>
            <FormControl>
              <RadioGroup
                aria-labelledby="motor-check-direction-label"
                value={direction}
                onChange={(_event, value) => setDirection(value as MotorDirection)}
              >
                <FormControlLabel
                  value={MOTOR_DIRECTION.forward}
                  disabled={isRunning || isStarting}
                  control={<Radio />}
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Trans i18nKey="motorCheckDialog.direction.forward">Forward</Trans>
                      {direction === MOTOR_DIRECTION.forward ? <ArrowUpwardIcon fontSize="small" aria-hidden /> : null}
                    </Stack>
                  }
                />
                <FormControlLabel
                  value={MOTOR_DIRECTION.reverse}
                  disabled={isRunning || isStarting}
                  control={<Radio />}
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Trans i18nKey="motorCheckDialog.direction.reverse">Reverse</Trans>
                      {direction === MOTOR_DIRECTION.reverse ? <ArrowDownwardIcon fontSize="small" aria-hidden /> : null}
                    </Stack>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Stack>

          <Stack spacing={1} alignItems="center">
            <Button
              variant="contained"
              size="large"
              onClick={isRunning ? handleStop : handleStart}
              disabled={port == null || !electronicType || (!isRunning && (rpm <= 0 || isStarting || commandActionsDisabled))}
              startIcon={isRunning ? <StopIcon /> : <PlayArrowIcon />}
            >
              {isRunning ? (
                <Trans i18nKey="motorCheckDialog.button.stop">Stop</Trans>
              ) : isStarting ? (
                <Trans i18nKey="motorCheckDialog.button.starting">Starting...</Trans>
              ) : (
                <Trans i18nKey="motorCheckDialog.button.start">Start</Trans>
              )}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button variant="contained" autoFocus onClick={handleClose} sx={{ alignSelf: 'flex-end' }}>
          <Trans i18nKey="motorCheckDialog.button.close">Close</Trans>
        </Button>
      </DialogActions>
    </>
  );
};

export default MotorCheckDialog;
